import React, { useEffect, useState } from "react";
import { Alert, Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { holdClubReservationApi } from "../../../api/api";
import useCourtSlotAvailability from "./useCourtSlotAvailability";
import CourtSlotAvailabilityPicker from "./CourtSlotAvailabilityPicker";

// Mirrors the court-namespaced key useCourtReservationFlow.js's Priority 2
// restore reads (never the bar flow's `reservation:pending_selection`).
const COURT_PENDING_SELECTION_KEY = "court_reservation:pending_selection";

/**
 * Listing-page quick-slot modal (design.md Decision 3/4). Fetches the same
 * `/:id/availability` payload the checkout grid uses, lets the buyer switch
 * duration tabs and pick a court for the pill's clicked start time, then
 * fires the hold client-side on "Elegir Turno":
 *   - authenticated -> holdClubReservationApi -> navigate(`/canchas/:slugOrId`,
 *     { state: { presetSelection } }) matching COURT_HOLD_STATE_KEY's shape.
 *   - 409 -> in-modal error, refreshes availability, no navigation.
 *   - unauthenticated / 401 -> COURT_PENDING_SELECTION_KEY + redirect to
 *     /login, letting the hook's existing Priority-2 restore re-hold.
 * Renders as a single react-bootstrap <Modal> for both desktop and mobile —
 * `quick-slot-modal-mobile` toggles a full-screen presentation via CSS,
 * reusing the same overlay primitive already used across `src/pages/canchas`.
 */
export default function QuickSlotModal({ show, club, slot, dateLabel, selectedSportFilter, onHide, isMobile = false }) {
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth?.accessToken);

  const [holdLoading, setHoldLoading] = useState(false);
  const [holdError, setHoldError] = useState("");

  const clubId = club?._id || null;
  const slugOrId = club?.slug || club?._id;

  const {
    loading,
    loadError,
    activeDuration,
    setActiveDuration,
    activeSport,
    setActiveSport,
    selectedCourtId,
    setSelectedCourtId,
    courtOptions,
    selectedOption,
    durations,
    sports,
    reload: loadAvailability,
  } = useCourtSlotAvailability({ clubId, dateLabel, slot, selectedSportFilter, club, show });

  useEffect(() => {
    if (!show) return;
    setHoldError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, clubId, dateLabel, selectedSportFilter]);

  const handleElegirTurno = async () => {
    if (!selectedOption) return;
    const { court, entry } = selectedOption;

    const buildPending = () => ({
      clubId,
      dateLabel,
      selectedDuration: activeDuration,
      selectedSport: court.sport,
      selectedCourtId: court.courtId,
      selectedStart: { slotStart: entry.slotStart, slotEnd: entry.slotEnd, price: entry.price },
    });

    if (!token) {
      try {
        sessionStorage.setItem(COURT_PENDING_SELECTION_KEY, JSON.stringify(buildPending()));
      } catch (_) {}
      navigate("/login", { state: { from: `/canchas/${slugOrId}` } });
      return;
    }

    try {
      setHoldLoading(true);
      setHoldError("");
      const response = await holdClubReservationApi(
        {
          clubId,
          courtId: court.courtId,
          date: dateLabel,
          slotStart: entry.slotStart,
          durationMinutes: activeDuration,
        },
        token
      );
      const info = response?.data?.info || {};
      const presetSelection = {
        ...buildPending(),
        holdId: info.holdId,
        holdExpiresAt: info.expiresAt,
      };
      navigate(`/canchas/${slugOrId}`, { state: { presetSelection } });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setHoldError(err?.response?.data?.message || "El turno se ocupó. Elegí otro horario.");
        setSelectedCourtId(null);
        await loadAvailability();
        return;
      }
      if (status === 401) {
        try {
          sessionStorage.setItem(COURT_PENDING_SELECTION_KEY, JSON.stringify(buildPending()));
        } catch (_) {}
        navigate("/login", { state: { from: `/canchas/${slugOrId}` } });
        return;
      }
      setHoldError(err?.response?.data?.message || "No pudimos bloquear el turno. Intentá de nuevo.");
    } finally {
      setHoldLoading(false);
    }
  };

  const ctaLabel = selectedOption
    ? `Elegir Turno ${selectedOption.entry.slotStart} - ${selectedOption.entry.slotEnd}`
    : "Elegir Turno";

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered={!isMobile}
      className={isMobile ? "quick-slot-modal quick-slot-modal-mobile" : "quick-slot-modal"}
      fullscreen={isMobile ? "sm-down" : undefined}
    >
      <Modal.Header closeButton>
        <Modal.Title>{club?.name || "Elegí tu turno"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {holdError && <Alert variant="danger">{holdError}</Alert>}
        <CourtSlotAvailabilityPicker
          sports={sports}
          activeSport={activeSport}
          onSportChange={(s) => {
            setActiveSport(s);
            setSelectedCourtId(null);
            setHoldError("");
          }}
          durations={durations}
          activeDuration={activeDuration}
          onDurationChange={(d) => {
            setActiveDuration(d);
            setSelectedCourtId(null);
            setHoldError("");
          }}
          loading={loading}
          loadError={loadError}
          courtOptions={courtOptions}
          selectedCourtId={selectedCourtId}
          onSelectCourt={(courtId) => setSelectedCourtId(courtId)}
          slot={slot}
        />
      </Modal.Body>
      <Modal.Footer className="justify-content-center">
        <button
          type="button"
          className="r-btn-primary popover-cta"
          disabled={!selectedOption || holdLoading}
          onClick={handleElegirTurno}
        >
          {holdLoading ? <Spinner animation="border" size="sm" /> : ctaLabel}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
